/**
 * PEDIT-TREE — data-acquisitions for ONE project, for the entity tree's DA leaf
 * layer.
 *
 * The DA resource lives in the lyse/v1 domain. apps/web reaches lyse endpoints
 * through the host `useApi()` axios instance (base-pinned to `/api/lyse/v1`) —
 * the same pattern the import/ai-model hooks use — rather than importing the
 * lyse app's internal query hook across the app-package boundary.
 *
 * The rows are typed as `DataAcquisitionListItem` (the codegenned
 * `DataAcquisition` overlaid with the additive `inspectionInstructionId` FK the
 * held BE branch serves — see `packages/types/src/inspection.ts`) so the tree
 * can link each DA under its inspection instruction.
 */
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { useAuthStore } from "@aeriosiq/auth-client";
import type { DataAcquisitionListItem } from "@aeriosiq/types";

interface DataAcquisitionsForProjectResponse {
  items: DataAcquisitionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 500;

export function useDataAcquisitionsForProject(projectId: string | null) {
  const api = useApi();
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantReady =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;

  return useQuery<DataAcquisitionsForProjectResponse>({
    queryKey: ["entity-tree", "data-acquisitions", projectId],
    queryFn: async () => {
      const response = await api.get<DataAcquisitionsForProjectResponse>(
        "/data-acquisitions",
        {
          params: {
            page: 1,
            pageSize: PAGE_SIZE,
            include_completed: true,
            project_id: projectId ? [projectId] : [],
          },
          paramsSerializer: { indexes: null },
        },
      );
      return response.data;
    },
    staleTime: 30_000,
    retry: false,
    enabled:
      tenantReady && typeof projectId === "string" && projectId.length > 0,
  });
}
